import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, TextField, MenuItem, IconButton, Tooltip, TableCell, TableRow,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import UndoIcon from '@mui/icons-material/Undo';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PartAttachments from './PartAttachments';
import { updateWorkItemPart, softDeleteWorkItemPart } from '../../actions/apiWorkItemParts';
import {
  AVAILABILITY, num, computeTotal, money, partFileAttachments, resolveUserName, isAdminUser,
} from './partsUtils';
import { userStamp } from '../../utils/dateFunctions';
import {
  truncatedPartNumberInputSx,
  truncatedPartDescriptionInputSx,
  PART_NUMBER_MAX_WIDTH,
  partDescriptionMaxWidthSx,
} from './TruncatedPartNumber';

const readableDisabled = {
  '& .Mui-disabled, & input.Mui-disabled, & textarea.Mui-disabled, & .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
    color: 'rgba(0, 0, 0, 0.87)',
    opacity: 1,
  },
};

const StatusPill = ({
  label, color, filled, tooltip,
}) => {
  const pill = (
    <Box
      component="span"
      sx={{
        display: 'inline-block',
        px: 1,
        py: '2px',
        borderRadius: '12px',
        fontSize: '0.75rem',
        fontWeight: 500,
        lineHeight: 1.5,
        whiteSpace: 'nowrap',
        cursor: tooltip ? 'help' : 'default',
        ...(filled
          ? { backgroundColor: color, color: '#fff' }
          : { border: `1px solid ${color}`, color }),
      }}
    >
      {label}
    </Box>
  );
  if (!tooltip) return pill;
  return (
    <Tooltip
      arrow
      title={tooltip}
      slotProps={{ tooltip: { sx: { maxWidth: 360, fontSize: '0.8rem', p: 1.25 } } }}
    >
      {pill}
    </Tooltip>
  );
};

const PartLineRow = ({
  part, vehicleId, uid, showFiles = false, columnCount = 8, locked = false,
}) => {
  const dispatch = useDispatch();
  const active_vehicle = useSelector(state => state.settings_refresh?.active_vehicle || {});
  const all_users = useSelector(state => state.settings_users?.all_users || []);
  const currentUser = useSelector(state => state.current_user || {});
  const currentUserProfile = useSelector(state => state.settings_users?.current_user_profile || {});
  const canUndo = !!(currentUser.approve_estimates || currentUserProfile.work_item_approve || isAdminUser(currentUser));
  const [expanded, setExpanded] = useState(false);
  const [declineReason, setDeclineReason] = useState(part.declined_reason || '');
  const [fields, setFields] = useState({
    part_number: part.part_number || '',
    description: part.description || '',
    price: part.price ?? '',
    quantity: part.quantity ?? 1,
  });

  const attachmentCount = useMemo(
    () => partFileAttachments(active_vehicle?.attachments, part.id).length,
    [active_vehicle, part.id],
  );

  const rowTotal = computeTotal(fields.price, fields.quantity);
  const lockInputs = !!part.approved || locked;

  const approvedName = resolveUserName(all_users, part.approved_by_uid);
  const approvedWhen = part.approved_by_date ? ` on ${userStamp(part.approved_by_date)}` : '';
  const approvedStamp = part.approved
    ? `${approvedName ? `Approved by ${approvedName}` : 'Auto approved'}${approvedWhen}`
    : '';
  const declinedName = resolveUserName(all_users, part.declined_by_uid);
  const deniedText = `Denied${declinedName ? ` by ${declinedName}` : ''}${part.declined_by_date ? ` on ${userStamp(part.declined_by_date)}` : ''}`;

  const patch = changes => dispatch(updateWorkItemPart(part.id, { vehicle_id: vehicleId, ...changes }));

  const setField = (field, value) => setFields(prev => ({ ...prev, [field]: value }));

  const persistText = field => () => {
    if ((fields[field] || '') !== (part[field] || '')) patch({ [field]: fields[field] });
  };

  const persistAmount = (field, { nullable = false } = {}) => () => {
    const value = num(fields[field], nullable);
    const price = field === 'price' ? value : num(fields.price, true);
    const quantity = field === 'quantity' ? value : num(fields.quantity);
    patch({ [field]: value, total: computeTotal(price, quantity) });
  };

  const approve = () => patch({
    approved: true, approved_by_uid: uid, approved_by_date: new Date().toISOString(), declined: false,
  });

  const undoApproval = () => patch({
    approved: false, approved_by_uid: null, approved_by_date: null,
  });

  const undoDecline = () => patch({
    declined: false, declined_by_uid: null, declined_by_date: null, declined_reason: '',
  });

  const decline = () => patch({
    declined: true, declined_by_uid: uid, declined_by_date: new Date().toISOString(), approved: false,
  });

  const persistDeclineReason = () => {
    if ((declineReason || '') !== (part.declined_reason || '')) patch({ declined_reason: declineReason });
  };

  const remove = () => {
    if (!window.confirm(`Remove part ${part.part_number}?`)) return;
    dispatch(softDeleteWorkItemPart(part.id, vehicleId));
  };

  return (
    <>
      <TableRow>
        {showFiles && (
          <TableCell sx={{ whiteSpace: 'nowrap', width: 80 }}>
            <Tooltip title={expanded ? 'Hide files' : 'Show files'}>
              <IconButton size="small" onClick={() => setExpanded(prev => !prev)}>
                {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            {attachmentCount > 0 && (
              <Tooltip title={`${attachmentCount} file${attachmentCount === 1 ? '' : 's'} attached`}>
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', color: 'success.main' }}>
                  <AttachFileIcon fontSize="small" />
                  <Typography variant="caption">{attachmentCount}</Typography>
                </Box>
              </Tooltip>
            )}
          </TableCell>
        )}
        <TableCell sx={{ minWidth: 150, maxWidth: PART_NUMBER_MAX_WIDTH, width: PART_NUMBER_MAX_WIDTH }}>
          <TextField
            variant="standard"
            fullWidth
            disabled={lockInputs}
            value={fields.part_number}
            onChange={e => setField('part_number', e.target.value)}
            onBlur={persistText('part_number')}
            inputProps={{ title: fields.part_number }}
            sx={{ ...readableDisabled, ...truncatedPartNumberInputSx }}
          />
        </TableCell>
        <TableCell sx={{ minWidth: 140, maxWidth: partDescriptionMaxWidthSx, width: partDescriptionMaxWidthSx }}>
          <TextField
            variant="standard"
            fullWidth
            disabled={lockInputs}
            value={fields.description}
            onChange={e => setField('description', e.target.value)}
            onBlur={persistText('description')}
            inputProps={{ title: fields.description }}
            sx={{ ...readableDisabled, ...truncatedPartDescriptionInputSx }}
          />
        </TableCell>
        <TableCell align="right">
          <TextField variant="standard" type="number" disabled={lockInputs} value={fields.price} placeholder="No price" onChange={e => setField('price', e.target.value)} onBlur={persistAmount('price', { nullable: true })} sx={{ width: 80, ...readableDisabled }} />
        </TableCell>
        <TableCell align="right">
          <TextField variant="standard" type="number" disabled={lockInputs} value={fields.quantity} onChange={e => setField('quantity', e.target.value)} onBlur={persistAmount('quantity')} sx={{ width: 60, ...readableDisabled }} />
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          <strong>${money(rowTotal)}</strong>
        </TableCell>
        <TableCell>
          <TextField variant="standard" select disabled={lockInputs} value={part.availability || ''} onChange={e => patch({ availability: e.target.value })} sx={{ width: 100, ...readableDisabled }}>
            <MenuItem value=""><em>—</em></MenuItem>
            {AVAILABILITY.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
        </TableCell>
        <TableCell sx={{ minWidth: 200 }}>
          {(() => {
            if (part.approved) {
              return (
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap',
                }}
                >
                  <button className="btn btn-sm btn-primary text-capitalize" style={{ whiteSpace: 'normal', textAlign: 'left', maxWidth: '260px' }}>
                    {approvedStamp}
                  </button>
                  {!locked && canUndo && (
                    <button className="btn btn-sm btn-warning" onClick={undoApproval}>Undo</button>
                  )}
                </Box>
              );
            }
            if (part.declined) {
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap',
                  }}
                  >
                    <button className="btn btn-sm btn-danger" style={{ whiteSpace: 'normal', textAlign: 'left', maxWidth: '260px' }}>
                      {deniedText}
                    </button>
                    {!locked && canUndo && (
                      <button className="btn btn-sm btn-warning" onClick={undoDecline}>Undo</button>
                    )}
                  </Box>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: '0.4rem', maxWidth: 260,
                  }}
                  >
                    <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>Notes:</Typography>
                    <TextField
                      variant="outlined"
                      size="small"
                      fullWidth
                      disabled={locked}
                      value={declineReason}
                      onChange={e => setDeclineReason(e.target.value)}
                      onBlur={persistDeclineReason}
                      sx={readableDisabled}
                    />
                  </Box>
                </Box>
              );
            }
            if (locked) {
              return <button className="btn btn-sm btn-secondary" disabled>Pending</button>;
            }
            return (
              <div className="btn-group" role="group">
                <button className="btn btn-sm btn-primary" onClick={approve}>Approve</button>
                <button className="btn btn-sm btn-danger" onClick={decline}>Deny</button>
              </div>
            );
          })()}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {!locked && !part.approved && (
            <Tooltip title="Remove"><IconButton size="small" color="error" onClick={remove}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
          )}
        </TableCell>
      </TableRow>
      {showFiles && expanded && (
        <TableRow>
          <TableCell colSpan={columnCount} sx={{ py: 1 }}>
            <PartAttachments part={part} vehicleId={vehicleId} />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default PartLineRow;
